import { cleanAndNormalizeCaseForSave } from '../utils/CaseUtils'
import { getToken } from '../utils/getToken'

import { wrapFetch } from './FetchHelper'

export default {
  toggleCaseStatus,
  get,
  getFiles,
  getAll,
  create,
  update,
  getSummary,
  getInProgressReport,
  getReport,
  getReportsForCase,
  getReportsByStatus,
  completeIntake,
  submitReport,
  approveReport,
  rejectReport,
  getReportFiles,
  addMembers,
  removeMembers,
  updateReport,
  markReportInvoiced,
  draftAppointmentsNarrative,

  resetReportingCycle,
  updateReportingSettings,
  unlockReport,
  regenerateSnapshot,
  deleteReport,

  delete: deleteCase,
  smartIntake,
  warmupSmartIntake,
  smartPaste,
  generateReviewerLink,
  downloadStatusReport,
  refreshMetadata,
  cancelSmartUpload,

  triggerSmartReview,
  cancelSmartReview,
  auditSmartReviewSuggestion,
  getSmartReviewPrompt,
  saveSmartReviewPrompt,
  testSmartReviewPrompt,
  getAppointmentsNarrativePrompt,
  saveAppointmentsNarrativePrompt,
  testAppointmentsNarrativePrompt,
  getRichTextFormattingPrompt,
  saveRichTextFormattingPrompt,
  testRichTextFormattingPrompt,

  getCaseHygiene,
  mergeDiagnoses,
  mergeProviders,
  applySmartCaseReview,
  logBillableActivity,
}

async function handleSuccess(result) {
  if (result.ok) return result.json()

  const errorText = await result.text().catch(() => 'No response body')

  const err = new Error(
    result.status === 403
      ? `Access forbidden (403). Response: ${errorText}`
      : result.status === 404
      ? `Resource not found (404). Response: ${errorText}`
      : `HTTP ${result.status}: ${result.statusText}. Response: ${errorText}`,
  )
  err.status = result.status
  throw err
}

function handleFailure(defaultErrorMessage, err) {
  if (err && err.message) {
    throw err
  }
  const error = new Error(defaultErrorMessage)
  if (err && err.status) error.status = err.status
  throw error
}

function get(caseId, fields) {
  let url = `/api/cases/${caseId}`

  if (fields && fields.length > 0) {
    url += `?$select=${fields.join(',')}`
  }

  return fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${getToken()}`,
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
  })
    .then(handleSuccess)
    .catch(err => handleFailure('Unable to retrieve case.', err))
}

function getFiles(caseNumber) {
  return wrapFetch(
    fetch(`/api/cases/${caseNumber}/files`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${getToken()}`,
        Accept: 'application/json',
      },
    }),
  ).onSuccess(json => json)
}

function toggleCaseStatus(caseNumber) {
  return wrapFetch(
    fetch(`/api/Cases/${caseNumber}/toggle-status`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${getToken()}`,
        Accept: 'application/json',
      },
      body: JSON.stringify(caseNumber),
    }),
  )
    .then(json => json)
    .catch(err => handleFailure('Unable to close case.', err))
}

function getAll(fields) {
  let url = '/api/cases'
  if (fields) {
    if (Array.isArray(fields) && fields.length > 0) {
      url += `?$select=${fields.join(',')}`
    } else {
      url += `?$select=${fields}`
    }
  }

  return fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${getToken()}`,
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
  })
    .then(handleSuccess)
    .catch(err => handleFailure('Unable to retrieve cases.', err))
}

function update(caseObj) {
  const caseNum = caseObj?.caseNumber || caseObj?.caseId
  if (!caseNum) {
    // eslint-disable-next-line no-console
    console.error('CasesService.update called without valid caseNumber:', caseObj)
    return Promise.reject(new Error('Cannot update case: missing caseNumber'))
  }
  const sanitizedCase = cleanAndNormalizeCaseForSave(caseObj)
  return wrapFetch(
    fetch(`/api/cases/${caseNum}`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${getToken()}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(sanitizedCase),
    }),
  )
}

function create(caseObj) {
  const sanitizedCase = cleanAndNormalizeCaseForSave(caseObj)
  return wrapFetch(
    fetch(`/api/Cases`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${getToken()}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(sanitizedCase),
    }),
  )
}

function completeIntake(caseNumber, tasks) {
  return wrapFetch(
    fetch(`/api/cases/${caseNumber}/complete-intake`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${getToken()}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        tasks,
      }),
    }),
  )
}

function getSummary(caseNumber) {
  return wrapFetch(
    fetch(`/api/cases/${caseNumber}/summary`, {
      headers: {
        Authorization: `Bearer ${getToken()}`,
      },
    }),
  )
}

async function getInProgressReport(caseNumber) {
  const response = await fetch(`/api/cases/${caseNumber}/reports/in-progress`, {
    headers: {
      Authorization: `Bearer ${getToken()}`,
    },
  })

  if (response.status === 403 || response.status === 404) return null

  return handleSuccess(response)
}

function getReport(caseNumber, reportNumber) {
  return wrapFetch(
    fetch(`/api/cases/${caseNumber}/reports/${reportNumber}`, {
      headers: {
        Authorization: `Bearer ${getToken()}`,
      },
    }),
  )
}

function getReportsForCase(caseNumber, fields) {
  let url = `/api/cases/${caseNumber}/reports`

  if (fields) {
    url += `?$select=${fields.join(',')}`
  }

  return wrapFetch(
    fetch(url, {
      headers: {
        Authorization: `Bearer ${getToken()}`,
      },
    }),
  )
}

function getReportsByStatus(status, fields) {
  let url = `/api/reports?$filter=status+eq+"${encodeURIComponent(status)}"`

  if (fields) {
    url += `&$select=${encodeURIComponent(fields.join(','))}`
  }

  return wrapFetch(
    fetch(url, {
      headers: {
        Authorization: `Bearer ${getToken()}`,
      },
    }),
  )
}

function getReportFiles(caseNumber, reportNumber) {
  return wrapFetch(
    fetch(`/api/cases/${caseNumber}/reports/${reportNumber}/files`, {
      headers: {
        Authorization: `Bearer ${getToken()}`,
      },
    }),
  )
}

function submitReport(caseNumber, submissionNote) {
  return wrapFetch(
    fetch(`/api/cases/${caseNumber}/reports/submit`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${getToken()}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ submissionNote }),
    }),
  )
}

function approveReport(caseNumber, reportNumber, invoiceNumber) {
  return wrapFetch(
    fetch(`/api/cases/${caseNumber}/reports/${reportNumber}/approve`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${getToken()}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ invoiceNumber }),
    }),
  )
}

function rejectReport(caseNumber, reportNumber, rejectReason) {
  return wrapFetch(
    fetch(`/api/cases/${caseNumber}/reports/${reportNumber}/reject`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${getToken()}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        rejectReason,
      }),
    }),
  )
}

function addMembers(caseNumber, members) {
  return fetch(`/api/cases/${caseNumber}/add-members`, {
    method: 'PUT',
    body: JSON.stringify({ members }),
    headers: {
      Authorization: `Bearer ${getToken()}`,
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
  })
    .then(handleSuccess)
    .catch(err => handleFailure('Unable to add members to case.', err))
}

function removeMembers(caseNumber, members) {
  return fetch(`/api/cases/${caseNumber}/remove-members`, {
    method: 'PUT',
    body: JSON.stringify({ members }),
    headers: {
      Authorization: `Bearer ${getToken()}`,
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
  })
    .then(handleSuccess)
    .catch(err => handleFailure('Unable to remove members from case.', err))
}

function deleteCase(caseNumber) {
  return fetch(`/api/cases/${caseNumber}`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${getToken()}`,
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
  }).then(response => {
    if (response.ok) {
      return // Success (resolve with undefined)
    }
    // Handle errors similar to wrapFetch or simple throw
    if (response.status === 403) {
      throw new Error(`Access forbidden to this case (403).`)
    }
    if (response.status === 404) {
      throw new Error(`Case not found (404).`)
    }
    throw new Error(`HTTP ${response.status}: ${response.statusText}`)
  })
}

function smartIntake(filesOrFile, caseData) {
  const formData = new FormData()
  const fileList = Array.isArray(filesOrFile)
    ? filesOrFile
    : typeof FileList !== 'undefined' && filesOrFile instanceof FileList
    ? Array.from(filesOrFile)
    : filesOrFile
    ? [filesOrFile]
    : []
  fileList.forEach(f => {
    formData.append('files', f)
  })
  if (caseData) {
    formData.append('caseJson', JSON.stringify(caseData))
  }

  // In local dev, bypass the webpack-dev-server proxy (which truncates large
  // multipart bodies) by sending directly to the API. CORS is open on the API.
  // REACT_APP_API_BASE_URL is set in .env.development; empty in production.
  const apiBase = process.env.REACT_APP_API_BASE_URL || ''

  // When bypassing the proxy, add X-Forwarded-Host so the API's UseForwardedHeaders()
  // middleware rewrites Request.Host before Finbuckle resolves the tenant.
  // This ensures tenant resolution is identical to going through the proxy.
  const extraHeaders = apiBase ? { 'X-Forwarded-Host': window.location.host } : {}

  return wrapFetch(
    fetch(`${apiBase}/api/intake/smart-upload`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${getToken()}`,
        Accept: 'application/json',
        ...extraHeaders,
      },
      body: formData,
    }),
  )
}

function warmupSmartIntake() {
  const apiBase = process.env.REACT_APP_API_BASE_URL || ''
  const extraHeaders = apiBase ? { 'X-Forwarded-Host': window.location.host } : {}
  fetch(`${apiBase}/api/cases/smart-upload/warmup`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${getToken()}`,
      Accept: 'application/json',
      ...extraHeaders,
    },
  }).catch(() => {}) // Fire and forget
}

/**
 * Smart Paste ✨ — sends a pasted email body to Bedrock and returns extracted
 * referral metadata: { referralDate, adjusterName, adjusterEmail, adjusterPhone,
 * claimNumber, dueDate }. All fields are nullable strings.
 */
function smartPaste(text) {
  const apiBase = process.env.REACT_APP_API_BASE_URL || ''
  const extraHeaders = apiBase ? { 'X-Forwarded-Host': window.location.host } : {}
  return wrapFetch(
    fetch(`${apiBase}/api/smartpaste`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${getToken()}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...extraHeaders,
      },
      body: JSON.stringify({ text }),
    }),
  )
}

function generateReviewerLink(caseNumber) {
  return wrapFetch(
    fetch(`/api/cases/${caseNumber}/reviewer-link`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${getToken()}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    }),
  )
}

/**
 * Fetches the Rule 16 Status Report PDF and triggers a browser download.
 * Returns a Promise that resolves when the download has been initiated.
 */
async function downloadStatusReport(caseNumber) {
  const res = await fetch(`/api/cases/${caseNumber}/status-report`, {
    headers: {
      Authorization: `Bearer ${getToken()}`,
      Accept: 'application/pdf',
    },
  })
  if (!res.ok) throw new Error(`Status report failed: HTTP ${res.status}`)
  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `Rule16_StatusReport_${caseNumber}.pdf`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

function refreshMetadata(caseNumber) {
  return wrapFetch(
    fetch(`/api/cases/${caseNumber}/refresh-metadata`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${getToken()}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    }),
  )
}

function cancelSmartUpload(caseNumber) {
  return wrapFetch(
    fetch(`/api/cases/${caseNumber}/smart-upload/cancel`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${getToken()}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    }),
  )
}

function updateReport(caseNumber, reportNumber, updateData) {
  return wrapFetch(
    fetch(`/api/cases/${caseNumber}/reports/${reportNumber}`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${getToken()}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(updateData),
    }),
  )
}

function markReportInvoiced(caseNumber, reportNumber, invoiceData) {
  return wrapFetch(
    fetch(`/api/cases/${caseNumber}/reports/${reportNumber}/invoice`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${getToken()}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(invoiceData),
    }),
  )
}

function draftAppointmentsNarrative(caseNumber, reportNumber) {
  return wrapFetch(
    fetch(`/api/cases/${caseNumber}/reports/${reportNumber}/draft-appointments-narrative`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${getToken()}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    }),
  )
}

function resetReportingCycle(caseNumber) {
  return wrapFetch(
    fetch(`/api/diagnostics/cases/${caseNumber}/reset-reporting-cycle`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${getToken()}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    }),
  )
}

function updateReportingSettings(caseNumber, settings) {
  return wrapFetch(
    fetch(`/api/diagnostics/cases/${caseNumber}/update-reporting-settings`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${getToken()}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(settings),
    }),
  )
}

function unlockReport(caseNumber, reportNumber) {
  return wrapFetch(
    fetch(`/api/diagnostics/cases/${caseNumber}/reports/${reportNumber}/unlock`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${getToken()}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    }),
  )
}

function regenerateSnapshot(caseNumber, reportNumber) {
  return wrapFetch(
    fetch(`/api/diagnostics/cases/${caseNumber}/reports/${reportNumber}/regenerate-snapshot`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${getToken()}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    }),
  )
}

function deleteReport(caseNumber, reportNumber) {
  return wrapFetch(
    fetch(`/api/diagnostics/cases/${caseNumber}/reports/${reportNumber}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${getToken()}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    }),
  )
}

function triggerSmartReview(caseNumber, reportNumber) {
  return wrapFetch(
    fetch(`/api/cases/${caseNumber}/reports/${reportNumber}/smart-review/trigger`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${getToken()}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    }),
  )
}

function cancelSmartReview(caseNumber, reportNumber) {
  return wrapFetch(
    fetch(`/api/cases/${caseNumber}/reports/${reportNumber}/smart-review/cancel`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${getToken()}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    }),
  )
}

function auditSmartReviewSuggestion(caseNumber, reportNumber, logData) {
  return wrapFetch(
    fetch(`/api/cases/${caseNumber}/reports/${reportNumber}/smart-review/audit`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${getToken()}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(logData),
    }),
  )
}

function getSmartReviewPrompt() {
  return wrapFetch(
    fetch('/api/diagnostics/smart-review/prompt', {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${getToken()}`,
        Accept: 'application/json',
      },
    }),
  )
}

function saveSmartReviewPrompt(prompt) {
  return wrapFetch(
    fetch('/api/diagnostics/smart-review/save-prompt', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${getToken()}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ prompt }),
    }),
  )
}

function testSmartReviewPrompt(caseNumber, reportNumber, prompt) {
  return wrapFetch(
    fetch('/api/diagnostics/smart-review/test-prompt', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${getToken()}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ caseNumber, reportNumber, prompt }),
    }),
  )
}

function getAppointmentsNarrativePrompt() {
  return wrapFetch(
    fetch('/api/diagnostics/appointments-narrative/prompt', {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${getToken()}`,
        Accept: 'application/json',
      },
    }),
  )
}

function saveAppointmentsNarrativePrompt(prompt) {
  return wrapFetch(
    fetch('/api/diagnostics/appointments-narrative/save-prompt', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${getToken()}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ prompt }),
    }),
  )
}

function testAppointmentsNarrativePrompt(caseNumber, reportNumber, prompt) {
  return wrapFetch(
    fetch('/api/diagnostics/appointments-narrative/test-prompt', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${getToken()}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ caseNumber, reportNumber, prompt }),
    }),
  )
}

function getRichTextFormattingPrompt() {
  return wrapFetch(
    fetch('/api/diagnostics/rich-text-formatting/prompt', {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${getToken()}`,
        Accept: 'application/json',
      },
    }),
  )
}

function saveRichTextFormattingPrompt(prompt) {
  return wrapFetch(
    fetch('/api/diagnostics/rich-text-formatting/save-prompt', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${getToken()}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ prompt }),
    }),
  )
}

function testRichTextFormattingPrompt(sampleText, prompt) {
  return wrapFetch(
    fetch('/api/diagnostics/rich-text-formatting/test-prompt', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${getToken()}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ sampleText, prompt }),
    }),
  )
}

function getCaseHygiene(caseNumber) {
  return wrapFetch(
    fetch(`/api/cases/${caseNumber}/hygiene`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${getToken()}`,
        Accept: 'application/json',
      },
    }),
  )
}

function mergeDiagnoses(caseNumber, payload) {
  return wrapFetch(
    fetch(`/api/cases/${caseNumber}/hygiene/merge-diagnoses`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${getToken()}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    }),
  )
}

function mergeProviders(caseNumber, payload) {
  return wrapFetch(
    fetch(`/api/cases/${caseNumber}/hygiene/merge-providers`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${getToken()}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    }),
  )
}

function applySmartCaseReview(caseNumber, payload) {
  return wrapFetch(
    fetch(`/api/cases/${caseNumber}/smart-review/apply`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${getToken()}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    }),
  )
}

function logBillableActivity(caseNumber, activity = {}) {
  const actType = activity.type || 'Coordination of Services'
  const numericHours = Math.max(0.1, Math.round((parseFloat(activity.time) || 0.2) * 10) / 10)
  const rawDate = activity.date || new Date().toISOString().split('T')[0]
  const isoDate = rawDate.includes('T') ? rawDate : new Date(`${rawDate}T12:00:00.000Z`).toISOString()
  const cleanDesc = (activity.description || `${actType} logged on case`).trim()
  const taskTitle = (activity.title || `${actType}: ${cleanDesc}`).slice(0, 80)

  const newTaskPayload = {
    caseNumber: Number(caseNumber),
    title: taskTitle,
    description: cleanDesc,
    note: cleanDesc,
    complete: true,
    dueDateUtc: isoDate,
    activities: [
      {
        activityNumber: 0,
        date: isoDate,
        type: actType,
        time: numericHours,
        description: cleanDesc,
      },
    ],
    expenses: [],
  }

  return wrapFetch(
    fetch(`/api/Cases/${caseNumber}/tasks`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${getToken()}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(newTaskPayload),
    }),
  )
}
